import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

import moment from 'moment';

const VenuesDailyDemandContext = createContext();

export const useVenuesDailyDemand = () => {
    const context = useContext(VenuesDailyDemandContext);

    if (!context) {
        throw new Error('useVenuesDailyDemand must be used within a VenuesDailyDemandContext');
    }

    return context;
};

const clampToRange = (value, min, max) => {
    if (min && value.isBefore(min, 'day')) {
        return min.clone();
    }
    if (max && value.isAfter(max, 'day')) {
        return max.clone();
    }
    return value.clone();
};

export const VenuesDailyDemandProvider = ({
    venueDailyDemandIndexUrl,
    venueDailyDemandUpsertUrl,
    venueSportsIndexUrl,
    venuesIndexUrl,
    eventStartDate,
    eventEndDate,
    orgStartDate,
    orgEndDate,
    children,
}) => {
    const [venues, setVenues] = useState([]);
    const [selectedVenue, setSelectedVenue] = useState(null);
    const [venueDailyDemands, setVenueDailyDemands] = useState([]);
    const [loading, setLoading] = useState(false);

    const minDate = useMemo(() => (orgStartDate ? moment(orgStartDate).startOf('day') : null), [orgStartDate]);
    const maxDate = useMemo(() => (orgEndDate ? moment(orgEndDate).startOf('day') : null), [orgEndDate]);

    const defaultRange = useMemo(() => {
        if (!eventStartDate || !eventEndDate) {
            return { start: null, end: null };
        }
        const start = clampToRange(moment(eventStartDate).startOf('day'), minDate, maxDate);
        const end = clampToRange(moment(eventEndDate).startOf('day'), minDate, maxDate);
        if (start.isAfter(end, 'day')) {
            return { start, end: clampToRange(start.clone().add(6, 'days'), minDate, maxDate) };
        }
        return { start, end };
    }, [eventStartDate, eventEndDate, minDate, maxDate]);

    const [range, setRange] = useState(defaultRange);

    useEffect(() => {
        fetchVenues();
    }, []);

    useEffect(() => {
        if (venues?.length && !selectedVenue) {
            setSelectedVenue(venues[0]);
        }
    }, [venues]);

    useEffect(() => {
        if (selectedVenue && range.start && range.end) {
            fetchVenueDailyDemands();
        }
    }, [selectedVenue, range.start?.format?.('YYYY-MM-DD'), range.end?.format?.('YYYY-MM-DD')]);

    const fetchVenues = () => {
        axios.get(venuesIndexUrl).then(({ data }) => {
            // Sort alphabetically — server returns competition-first ordering
            // but the venue picker for this view should be plain A→Z.
            const sorted = [...data].sort((a, b) => (a?.name ?? '').localeCompare(b?.name ?? '', undefined, { sensitivity: 'base' }));
            setVenues(sorted);
        });
    };

    const fetchVenueDailyDemands = () => {
        const venueSlug = selectedVenue.slug;
        setLoading(true);

        axios
            .get(venueDailyDemandIndexUrl.replace('VENUE_SLUG', venueSlug), {
                params: {
                    start_date: range.start.format('YYYY-MM-DD'),
                    end_date: range.end.format('YYYY-MM-DD'),
                },
            })
            .then(({ data }) => {
                // Preserve the existing ordering: Spectators + Workforce first,
                // then the remaining categories (Teams, Technical Officials, etc.).
                const filtered = data.filter((item) => ['Spectators', 'Workforce'].includes(item.name));
                const rest = data.filter((item) => !['Spectators', 'Workforce'].includes(item.name));
                rest.unshift(...filtered);

                setVenueDailyDemands(rest);
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const fetchVenueSports = (venueSlug, date) => {
        return axios.get(venueSportsIndexUrl.replace('VENUE_SLUG', venueSlug), { params: { date } });
    };

    const upsertDailyDemand = (data) => {
        return axios.put(venueDailyDemandUpsertUrl.replace('VENUE_SLUG', selectedVenue.slug), data);
    };

    const updateDailyDemandByKey = (key, data) => {
        setVenueDailyDemands((demands) => {
            return demands.map((demand) => (demand.key === key ? { ...demand, ...data } : demand));
        });
    };

    /**
     * Set the active range. Validates start <= end; if not, falls back to a
     * 7-day window starting at the requested start.
     */
    const updateRange = (nextStart, nextEnd) => {
        if (!nextStart) {
            return;
        }
        let start = clampToRange(moment(nextStart).startOf('day'), minDate, maxDate);
        let end = nextEnd ? clampToRange(moment(nextEnd).startOf('day'), minDate, maxDate) : null;

        if (!end || end.isBefore(start, 'day')) {
            end = clampToRange(start.clone().add(6, 'days'), minDate, maxDate);
        }

        setRange({ start, end });
    };

    /**
     * Step the range forward/back by its full width (page-flip).
     * Direction: +1 forward, -1 back. Clamped at organisation bounds.
     */
    const stepRange = (direction) => {
        if (!range.start || !range.end) {
            return;
        }
        const widthDays = range.end.diff(range.start, 'days') + 1;
        let nextStart = range.start.clone().add(direction * widthDays, 'days');
        let nextEnd = range.end.clone().add(direction * widthDays, 'days');

        // Clamp without changing range width when hitting boundaries.
        if (minDate && nextStart.isBefore(minDate, 'day')) {
            nextStart = minDate.clone();
            nextEnd = nextStart.clone().add(widthDays - 1, 'days');
        }
        if (maxDate && nextEnd.isAfter(maxDate, 'day')) {
            nextEnd = maxDate.clone();
            nextStart = nextEnd.clone().subtract(widthDays - 1, 'days');
            if (minDate && nextStart.isBefore(minDate, 'day')) {
                nextStart = minDate.clone();
            }
        }

        setRange({ start: nextStart, end: nextEnd });
    };

    const value = {
        venues,
        selectedVenue,
        setSelectedVenue,
        venueDailyDemands,
        venueDailyDemandUpsertUrl,
        eventStartDate,
        orgStartDate,
        orgEndDate,
        minDate,
        maxDate,
        range,
        setRange: updateRange,
        stepRange,
        loading,
        upsertDailyDemand,
        updateDailyDemandByKey,
        fetchVenueSports,
    };

    return <VenuesDailyDemandContext.Provider value={value}>{children}</VenuesDailyDemandContext.Provider>;
};

export default VenuesDailyDemandContext;
